import { useCallback, useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { Route as LancRoute } from '@/routes/m.lancamentos';
import { usePagamentos, usePagamentosAggregate } from '@/lib/hooks/pagamentos';
import { useAuth } from '@/lib/auth';
import { MobileTopbar } from '@/mobile/shared/MobileTopbar';
import { useMobileScan } from '@/mobile/shell/MobileScanContext';
import { LancDetailModule } from './LancDetailModule';
import { LancKPICards } from './LancKPICards';
import { LancSearchList } from './LancSearchList';
import type { Lancamento, LancFilters } from './types';
import type { Pagamento } from '@/lib/hooks/pagamentos';

const COMPETENCIA = '2026-05';
const MONTH_LABEL = 'maio 2026';

function toUiItem(p: Pagamento): Lancamento {
  const parts = (p.dataPagamento ?? '').split('-');
  const dateShort = parts.length >= 3 ? `${parts[2]}/${parts[1]}` : (p.dataPagamento ?? '—');
  return {
    id: p.id,
    forn: p.credorNome,
    cnpj: p.credorCpfCnpj,
    cat: p.categoriaDespesa ?? 'Outros',
    date: dateShort,
    emissao: p.dataPagamento ?? '—',
    venc: (p.metadata as Record<string, string>)?.vencimento ?? dateShort,
    pgto: p.formaPagamento ?? 'Transferência',
    val: Math.round(Number(p.valor) * 100) / 100,
    status: p.status === 'CONCILIADO' ? 'ok' : 'pending',
    ocr: !!(p.documentoFiscalId || p.comprovanteR2Key),
    shot: undefined,
  };
}

/**
 * Lista principal: topbar + KPIs + CTA scan + SearchList.
 * openScan vem de MobileScanContext — sem prop drilling.
 * initialFilters é injetado quando o usuário chega via Home > Pendências.
 */
function LancamentosList({
  onOpen,
  initialFilters,
}: {
  onOpen: (item: Lancamento) => void;
  initialFilters?: Partial<LancFilters>;
}) {
  const { user } = useAuth();
  const { openScan } = useMobileScan();
  const wsId = user?.effectiveWorkspaceId;

  const { data, isLoading } = usePagamentos(wsId, { competencia: COMPETENCIA, limit: 50 });
  const { data: agg } = usePagamentosAggregate(wsId, COMPETENCIA);

  const items: Lancamento[] = (data?.items ?? []).map(toUiItem);
  const pendCount = items.filter((i) => i.status !== 'ok').length;
  const concilCount = agg?.conciliado.count ?? 0;
  const totalCount = agg?.totalLancamentos ?? items.length;
  const aPagar = agg ? Number(agg.pendente.total) : 0;

  return (
    <div
      style={{
        padding: '8px 18px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <MobileTopbar title="Lançamentos" subtitle={`Pagamentos · ${MONTH_LABEL}`} />

      <LancKPICards
        aPagar={aPagar}
        pendCount={pendCount}
        concilCount={concilCount}
        totalCount={totalCount}
      />

      <button
        onClick={openScan}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '13px 18px',
          borderRadius: 'var(--r-3)',
          background: 'var(--mint-deep)',
          color: 'var(--on-ink)',
          fontSize: 14,
          fontWeight: 700,
          border: 'none',
          cursor: 'pointer',
          minHeight: 44,
        }}
      >
        <Camera size={17} strokeWidth={1.7} aria-hidden />
        Escanear nota / recibo
      </button>

      <LancSearchList
        items={items}
        isLoading={isLoading}
        onOpen={onOpen}
        initialFilters={initialFilters}
      />
    </div>
  );
}

/**
 * Orquestrador do módulo Lançamentos.
 * Nav state local: lista ↔ detalhe (sem rota nova).
 * Preserva scroll da lista ao voltar do detalhe.
 * Lê search params da rota para pré-aplicar filtros (via Home > Pendências).
 */
export function LancamentosModule() {
  const search = LancRoute.useSearch();
  const [selected, setSelected] = useState<Lancamento | null>(null);
  const scrollYRef = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);

  const handleOpen = useCallback((item: Lancamento) => {
    scrollYRef.current = listRef.current?.scrollTop ?? 0;
    setSelected(item);
  }, []);

  const handleBack = useCallback(() => {
    setSelected(null);
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = scrollYRef.current;
    });
  }, []);

  // Mapeia ?filter=pending → { tipo: 'pending' }
  const initialFilters: Partial<LancFilters> | undefined =
    search.filter === 'pending' ? { tipo: 'pending' } : undefined;

  if (selected) {
    return <LancDetailModule item={selected} onBack={handleBack} />;
  }

  return (
    <div ref={listRef} style={{ height: '100%', overflowY: 'auto', minHeight: 0 }}>
      <LancamentosList onOpen={handleOpen} initialFilters={initialFilters} />
    </div>
  );
}
